import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Ban } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { CreateObjectButton } from '@/components/CreateObjectButton'
import { BlocklistsApi, Blocklist } from '@/services/rules/blocklistsApi'
import { apiErrorDescription } from '@/services/core/apiError'
import { walkAllPages, type ListSort } from '@/services/core/paging'
import { AppShell } from '../../components/AppShell'
import { BlocklistTable } from '../../components/BlocklistTable'
import { BulkActionBar } from '../../components/BulkActionBar'
import { StatusFilterButton } from '../../components/StatusFilter'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { SearchInput } from '../../components/SearchInput'
import { CardContent, CardIcon } from '@/components/ui/card'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useUrlSearch } from '@/hooks/useUrlSearch'
import { useNetwork } from '@/contexts/NetworkContext'
import { ListPagination } from '../../components/ListPagination'
import { PageSizeSelect } from '../../components/PageSizeSelect'

export function BlocklistsPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [search, setSearch] = useUrlSearch()
  const { scopeKey, scopeReady } = useNetwork()
  const [statusFilter, setStatusFilter] = usePersistedState<'all' | 'ACTIVE' | 'INACTIVE'>('filters.blocklists.status', 'ACTIVE')
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<ListSort | null>(null)
  const [pageSize, setPageSize] = useState(25)
  const [selection, setSelection] = useState<Blocklist[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)

  // The list a page at a time, filtered and sorted as on screen.
  const fetchPage = (page: number, pageSize: number) =>
    BlocklistsApi.getPage({ page, pageSize, search, status: statusFilter, sort })

  const { data, isLoading } = useQuery({
    queryKey: ['blocklists', scopeKey, search, statusFilter, page, pageSize, sort],
    queryFn: () => fetchPage(page, pageSize),
    enabled: scopeReady,
    staleTime: 5 * 60 * 1000,
  })
  const blocklists = data?.results ?? []
  const totalCount = data?.totalCount ?? 0

  /** Back to the first page whenever the rows underneath change. */
  const refilter = (apply: () => void) => {
    apply()
    setPage(1)
    setSelection([])
  }

  const bulkSetStatus = async (state: 'ACTIVE' | 'INACTIVE') => {
    setBulkLoading(true)
    try {
      await Promise.all(selection.map((b) => BlocklistsApi.update(b.id, { name: b.name, objectState: state })))
      queryClient.invalidateQueries({ queryKey: ['blocklists'] })
      toast.success(t('common.done'), {
        description: t(state === 'ACTIVE' ? 'blocklists.doneDetail_activate' : 'blocklists.doneDetail_deactivate', { count: selection.length }),
        duration: 3000,
      })
      setSelection([])
    } catch (e) {
      toast.error(t('common.error'), { description: apiErrorDescription(e), duration: 5000 })
    } finally {
      setBulkLoading(false)
    }
  }

  const handleSort = (next: ListSort | null) => {
    setSort(next)
    setPage(1)
  }

  return (
    <AppShell crumbs={[{ label: t('blocklists.title') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('blocklists.title')}</h1>
            <p>{t('blocklists.subtitle')}</p>
          </div>
          <div className="ad-page__actions">
            <CreateObjectButton type="blocklist" size="default" />
          </div>
        </div>

        <ListCard fetchAll={() => walkAllPages(fetchPage)} tone="attention">
          <ListCardHeader
            title={
              <>
                <CardIcon icon={Ban} />
                {t('blocklists.title')}
              </>
            }
            count={totalCount}
            actions={<PageSizeSelect value={pageSize} onChange={(size) => refilter(() => setPageSize(size))} />}
            controls={
              <>
                <SearchInput value={search} onChange={(v) => refilter(() => setSearch(v))} />
                <StatusFilterButton value={statusFilter} onChange={(v) => refilter(() => setStatusFilter(v))} />
              </>
            }
          />

          <BulkActionBar
            count={selection.length}
            actions={[
              { label: t('common.activate'), icon: 'pi pi-check-circle', severity: 'success', onClick: () => bulkSetStatus('ACTIVE') },
              { label: t('common.deactivate'), icon: 'pi pi-ban', severity: 'danger', onClick: () => bulkSetStatus('INACTIVE') },
            ]}
            onClear={() => setSelection([])}
            loading={bulkLoading}
          />

          <CardContent className="p-0">
            <BlocklistTable
              blocklists={blocklists}
              loading={isLoading}
              selection={selection}
              onSelectionChange={setSelection}
              emptyMessage={t('blocklists.noBlocklistsFound')}
              sort={sort}
              onSortChange={handleSort}
            />
          </CardContent>

          <ListPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            summary={t('blocklists.blocklistCount', { count: totalCount })}
          />
        </ListCard>
      </div>
    </AppShell>
  )
}
